import React, { useEffect, useState } from 'react';
import './SelectField.scss';
import { FieldValues, Path, PathValue, useFormContext } from 'react-hook-form';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';

export type SelectOption = {
  id: string | number;
  name: string | number;
};

export type SelectProps<TFormValues extends FieldValues> = {
  name: Path<TFormValues>;
  options: SelectOption[];
  isRequired: boolean;
  editable: boolean;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'name'>;

const SelectField = <TFormValues extends FieldValues>({
  options = [],
  name,
  isRequired = false,
  editable = true,
  ...rest
}: SelectProps<TFormValues>): JSX.Element => {
  const { getValues, setValue, register } = useFormContext<TFormValues>();

  const defaultValue = getValues(name);
  const defaultOption = options.find((option) => option.id === defaultValue) || options[0];

  const [selectedOption, setSelectedOption] = useState(defaultOption);
  const [isActive, setIsActive] = useState(false);

  const toggleDropdown = (): void => {
    if (!editable) {
      return;
    }
    setIsActive(!isActive);
  };

  const handleOptionClick = (option: SelectOption): void => {
    setSelectedOption(option);
    setIsActive(false);
    setValue(name, option.id as PathValue<TFormValues, Path<TFormValues>>); // Fix the type error here
  };

  const handleBlur = (): void => {
    if (isActive) {
      setIsActive(!isActive);
    }
  };

  const handleClickOutside = (event: MouseEvent): void => {
    if (event.target instanceof HTMLElement && !event.target.closest('.select-wrap')) {
      setIsActive(false);
    }
  };

  useEffect(() => {
    const newSelectedOption = options.find((option) => option.id === defaultValue);
    if (newSelectedOption && (!selectedOption || newSelectedOption.id !== selectedOption.id)) {
      setSelectedOption(newSelectedOption);
    }
  }, [options, selectedOption, defaultValue]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActive]);

  return (
    <div
      className={`select-wrap${editable ? '' : ' not-editable'}`}
      onClick={toggleDropdown}
      onBlur={handleBlur}
      tabIndex={0}
    >
      <div className="flex-box">
        {isRequired && <span className="required-mark">*</span>}
        <div className="select-field">
          <div className="selected-option">{selectedOption?.name}</div>
          {isActive ? (
            <MdKeyboardArrowUp className="dropdown-icon" />
          ) : (
            <MdKeyboardArrowDown className="dropdown-icon" />
          )}
          {isActive && (
            <div className="options">
              {options.map((option) => (
                <div key={option.id} className="option" onClick={(): void => handleOptionClick(option)}>
                  {option.name}
                </div>
              ))}
            </div>
          )}
        </div>
        <input type="hidden" {...register(name)} value={selectedOption?.id} {...rest} />
      </div>
    </div>
  );
};

export default SelectField;
